import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { ArrowRight } from "lucide-react";

const EVENT_TYPES = [
  "Wedding", "Concert", "Festival", "City Event",
  "Corporate Event", "Private Party", "Film Production",
  "Sporting Event", "Other",
];

export default function QuickLeadForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    event_type: "Wedding", event_date: "", event_city: "",
    guest_count: "", trailers: "unsure",
  });
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email, and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
      };
      await api.post("/leads", payload);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", event_type: "Wedding", event_date: "", event_city: "", guest_count: "", trailers: "unsure" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-soft p-8 md:p-10" data-testid="quick-lead-form">
      <div className="overline mb-2">Get a quick estimate</div>
      <h3 className="h-display text-3xl md:text-4xl leading-tight">Planning an event? Let's start with the details.</h3>
      <p className="text-graphite mt-3 text-sm">Share a few details and we'll follow up with pricing and next steps.</p>

      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <Input label="Your name" value={form.name} onChange={upd("name")} tid={TID.qlfName} required />
        <Input label="Email" type="email" value={form.email} onChange={upd("email")} tid={TID.qlfEmail} required />
        <Input label="Phone" type="tel" value={form.phone} onChange={upd("phone")} tid={TID.qlfPhone} required />
        <Select label="Event type" value={form.event_type} onChange={upd("event_type")} tid={TID.qlfEventType} options={EVENT_TYPES} />
        <Input label="Event date" type="date" value={form.event_date} onChange={upd("event_date")} tid={TID.qlfEventDate} />
        <Input label="City" value={form.event_city} onChange={upd("event_city")} tid={TID.qlfEventCity} placeholder="St. George" />
        <Input label="Approx. guest count" type="number" min="0" value={form.guest_count} onChange={upd("guest_count")} tid={TID.qlfGuestCount} />
        <Select label="Trailers needed" value={form.trailers} onChange={upd("trailers")} tid={TID.qlfTrailers}
          options={[["one","One trailer (3 stalls)"],["two","Two trailers (6 stalls)"],["unsure","I'm not sure"]]}
        />
      </div>

      <button type="submit" disabled={submitting} data-testid={TID.qlfSubmit} className="btn-primary mt-8 w-full md:w-auto">
        {submitting ? "Sending..." : "Get my estimate"} <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
}

function Input({ label, tid, ...rest }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      <input
        {...rest}
        data-testid={tid}
        className="w-full h-12 rounded-2xl border border-stone bg-ivory px-4 text-charcoal placeholder:text-mist focus:outline-none focus:ring-2 focus:ring-pebble focus:border-pebble transition-shadow"
      />
    </label>
  );
}

function Select({ label, tid, options, ...rest }) {
  const norm = options.map((o) => Array.isArray(o) ? o : [o, o]);
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      <select
        {...rest}
        data-testid={tid}
        className="w-full h-12 rounded-2xl border border-stone bg-ivory px-4 text-charcoal focus:outline-none focus:ring-2 focus:ring-pebble focus:border-pebble transition-shadow"
      >
        {norm.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
